
import {of as observableOf,  Observable } from 'rxjs';

import {delay} from 'rxjs/operators';




export interface RetryDecision {
    (status : number, statusText : string, xhr : XMLHttpRequest, tryCount : number) : Promise<boolean>;
}

export interface Retryable<T> {
    (...args) : Promise<T>;
}

export interface ErrorHandler {
    (statusCode : number, statusText : string, xhr : XMLHttpRequest) : void;
}

export class RetryFailure extends Error {
    constructor(public status, public statusText, public xhr) {
        super("Failed to fetch resource");
    }
}

export class Retry {

    public static async standard<T>(f : Retryable<T>, errorHandler = null) {
        return await this.many(f, 5, 1000, errorHandler);
    }

    public static determineDelay(initialDelay : number, tryCount : number) {
        let maxTime = 3 * 60 * 1000;
        let deterministicTime = initialDelay * Math.pow(10, Math.min(3, Math.max(1, tryCount)));
        let randomTime = Math.random() * deterministicTime * 0.25;
        return Math.min(maxTime, deterministicTime) + randomTime;
    }

    public static async forever<T>(f : Retryable<T>, initialDelay : number = 1000, errorHandler = null) : Promise<T> {
        return Retry.retry(f, (status, statusText, xhr, tryCount) => {

            if (errorHandler) {
                errorHandler();
            } else {
                console.error(`Failed to fetch data (${status} ${statusText || 'No Response'})`);
                console.error('Request that caused this:');
                console.dir(xhr);
            }

            // Delay 

            if (initialDelay == 0)
                return Promise.resolve(true);
            
            let finalDelay = this.determineDelay(initialDelay, tryCount);
            console.error('Delaying retry by '+finalDelay+' ms');

            return observableOf(true).pipe(
                delay(finalDelay))
                .toPromise();
        });
    }

    public static async many<T>(f : Retryable<T>, count : number, initialDelay : number = 5000, errorHandler : ErrorHandler = null): Promise<T> {
        return await this.retry(f, (status, statusText, xhr, tryCount) => {

            if (errorHandler) {
                errorHandler(status, statusText, xhr);
            } else {
                console.error(`[Retryable] Failed to fetch data (${status} ${statusText || 'No Response'})`);
                console.error('Request that caused this:');
                console.dir(xhr);
            }

            if (--count < 0) 
                return Promise.resolve(false);

            // Delay 

            if (initialDelay == 0)
                return Promise.resolve(true);
            
            let finalDelay = this.determineDelay(initialDelay, tryCount);
            
            console.error('Delaying retry by '+finalDelay+' ms');

            return observableOf(true).pipe(
                delay(finalDelay))
                .toPromise();
        });
    }

    public static async once<T>(f : Retryable<T>, delay : number = 5000, errorHandler : ErrorHandler = null) : Promise<T> {
        return await this.many(f, 1, delay, errorHandler);
    }

    public static async twice<T>(f : Retryable<T>, delay : number = 5000, errorHandler : ErrorHandler = null): Promise<T> {
        return await this.many(f, 1, delay, errorHandler);
    }

    public static async retry<T>(f : Retryable<T>, discrim : RetryDecision, tryCount = 0): Promise<T> {
        try {
            return await f();
        } catch (e) {
            
            let retryCapable = false;
            let xhr : XMLHttpRequest = null;
            let status : number = -1;
            let statusText : string = null; 

            console.error("Failed to complete HTTP request:");
            console.error("Exception was:");
            console.dir(JSON.stringify(e));
            console.error("Request that caused this: " + f.toString());

            if (typeof ProgressEvent !== 'undefined' && e instanceof ProgressEvent) {
                // ProgressEvent as provided by XHR
                xhr = <any> e.currentTarget;
                status = xhr.status;
                statusText = xhr.statusText;
            } else if ('headers' in e) {
                // Response as provided by Angular HTTP
                xhr = <any> e['_body'].currentTarget;
                status = e.status;
                statusText = e.statusText;
            } else {
                // Not a known error type, throw it normally and do not retry.
                throw e;
            }

            if (status == 0) {
                // Failed to connect to API
                retryCapable = true;
            } else if (status >= 500 && status < 600) {
                // Server side error 
                retryCapable = true;
            }


            if (retryCapable) {
                tryCount += 1;
                let willRetry = await discrim(e.status, e.statusText, xhr, tryCount);
                
                if (!willRetry)
                    throw new RetryFailure(status, statusText, xhr);
                
                return await this.retry(f, discrim, tryCount);
            }
        }
    }
}